import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import {
  FiZap, FiTrendingUp, FiTrendingDown, FiMinus,
  FiAlertCircle, FiLock, FiTrash2, FiMail, FiChevronDown,
  FiRefreshCw, FiPlus,
} from 'react-icons/fi';
import { generateReport, deleteInsight, getInsights } from '../../services/aiInsights';
import { useSubscription } from '../../context/SubscriptionContext';

const SAVED_KEY = 'glowlytix_ai_saved';

function loadLocalReports() {
  try { return JSON.parse(localStorage.getItem(SAVED_KEY) || '[]'); }
  catch { return []; }
}

function saveLocalReports(list) {
  localStorage.setItem(SAVED_KEY, JSON.stringify(list.slice(0, 20)));
}

function buildEmailLink(item) {
  const { report, generatedAt, entryCount } = item;
  const date = new Date(generatedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const subject = encodeURIComponent(`My Skin Glow Insights – ${date}`);
  const lines = [
    `Skin Glow Insights · ${date} · ${entryCount} entries`,
    '',
    '📊 Summary',
    report?.summary || '',
    '',
  ];
  if (report?.topInsight) lines.push('💡 Key Finding', report.topInsight, '');
  if (report?.boosters?.length) lines.push('✅ Glow Boosters', ...report.boosters.map(b => `• ${b}`), '');
  if (report?.drainers?.length) lines.push('⚠️ To Watch', ...report.drainers.map(d => `• ${d}`), '');
  if (report?.recommendations?.length) lines.push('📋 Recommendations', ...report.recommendations.map((r, i) => `${i + 1}. ${r}`), '');
  lines.push('—', 'Tracked with Glowlytix');
  return `mailto:?subject=${subject}&body=${encodeURIComponent(lines.join('\n'))}`;
}

function TrendBadge({ trend }) {
  const map = {
    improving: { icon: FiTrendingUp, label: 'Improving', color: 'text-emerald-600', bg: 'bg-emerald-50' },
    declining:  { icon: FiTrendingDown, label: 'Declining', color: 'text-rose-500', bg: 'bg-rose-50' },
    stable:     { icon: FiMinus, label: 'Stable', color: 'text-stone-500', bg: 'bg-stone-100' },
  };
  const cfg = map[trend] || map.stable;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold font-sans ${cfg.color} ${cfg.bg}`}>
      <SafeIcon icon={cfg.icon} className="text-xs" />
      {cfg.label}
    </span>
  );
}

function ScoreDots({ score }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 10 }, (_, i) => (
        <div key={i} className="w-1.5 h-1.5 rounded-full"
          style={{ background: i < score ? '#7c3aed' : '#e7e5e4' }} />
      ))}
    </div>
  );
}

function LockBanner({ onOpenPricing }) {
  return (
    <div className="bg-white rounded-3xl border border-stone-100 shadow-sm overflow-hidden">
      <div className="p-5 relative">
        <div className="blur-sm pointer-events-none select-none mb-4 space-y-3">
          <div className="h-4 bg-stone-100 rounded-full w-3/4" />
          <div className="h-4 bg-stone-100 rounded-full w-full" />
          <div className="h-4 bg-stone-100 rounded-full w-2/3" />
          <div className="grid grid-cols-2 gap-3 mt-4">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-8 bg-stone-100 rounded-2xl" />)}
          </div>
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
          <div className="w-12 h-12 rounded-full bg-violet-50 border border-violet-200 flex items-center justify-center mb-3">
            <SafeIcon icon={FiLock} className="text-violet-500 text-lg" />
          </div>
          <p className="font-serif text-lg text-stone-900 mb-1">AI Skin Insights</p>
          <p className="font-sans text-xs text-stone-500 mb-4 leading-relaxed">
            Personalized reports and photo analysis powered by AI — exclusive to Radiant Pro.
          </p>
          <motion.button whileTap={{ scale: 0.97 }} onClick={onOpenPricing}
            className="px-6 py-3 rounded-full font-sans font-semibold text-white text-sm shadow-md"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}>
            Upgrade to Radiant Pro
          </motion.button>
        </div>
      </div>
    </div>
  );
}

function FullReportModal({ item, onClose, onDelete }) {
  const { report, generatedAt, entryCount } = item;
  const date = new Date(generatedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 30 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-50 flex flex-col bg-stone-50 overflow-y-auto"
      style={{ maxWidth: 430, margin: '0 auto' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-4 bg-white border-b border-stone-100 flex-shrink-0">
        <div>
          <p className="font-serif text-lg text-stone-900">AI Skin Report</p>
          <p className="font-sans text-xs text-stone-400">{date} · {entryCount} entries</p>
        </div>
        <button onClick={onClose}
          className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 active:bg-stone-200 text-lg leading-none">
          ×
        </button>
      </div>

      <div className="flex-1 px-5 py-4 space-y-4">
        {/* Trend + score */}
        <div className="bg-white rounded-2xl border border-stone-100 shadow-sm px-4 py-3.5 flex items-center justify-between">
          <TrendBadge trend={report.glowTrend} />
          <ScoreDots score={report.weeklyScore || 0} />
        </div>

        {/* Summary */}
        <div className="bg-white rounded-2xl border border-stone-100 shadow-sm px-4 py-3.5">
          <p className="font-sans text-xs font-semibold text-stone-400 uppercase tracking-widest mb-2">Summary</p>
          <p className="font-sans text-sm text-stone-700 leading-relaxed">{report.summary}</p>
        </div>

        {/* Key finding */}
        {report.topInsight && (
          <div className="rounded-2xl bg-violet-50 border border-violet-100 px-4 py-3.5">
            <p className="font-sans text-xs font-semibold text-violet-700 uppercase tracking-wider mb-1.5">Key Finding</p>
            <p className="font-sans text-sm text-violet-900 leading-relaxed">{report.topInsight}</p>
          </div>
        )}

        {/* Boosters + Drainers */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-2xl border border-stone-100 shadow-sm px-3 py-3.5">
            <div className="flex items-center gap-1.5 mb-2">
              <SafeIcon icon={FiTrendingUp} className="text-xs text-emerald-500" />
              <p className="font-sans text-xs font-semibold text-stone-700">Boosters</p>
            </div>
            {(report.boosters || []).map((b, i) => (
              <p key={i} className="font-sans text-xs text-stone-500 leading-snug mb-1">• {b}</p>
            ))}
          </div>
          <div className="bg-white rounded-2xl border border-stone-100 shadow-sm px-3 py-3.5">
            <div className="flex items-center gap-1.5 mb-2">
              <SafeIcon icon={FiTrendingDown} className="text-xs text-rose-400" />
              <p className="font-sans text-xs font-semibold text-stone-700">Drainers</p>
            </div>
            {(report.drainers || []).map((d, i) => (
              <p key={i} className="font-sans text-xs text-stone-500 leading-snug mb-1">• {d}</p>
            ))}
          </div>
        </div>

        {/* Recommendations */}
        {(report.recommendations || []).length > 0 && (
          <div className="bg-white rounded-2xl border border-stone-100 shadow-sm px-4 py-3.5">
            <p className="font-sans text-xs font-semibold text-stone-400 uppercase tracking-widest mb-3">Recommendations</p>
            <div className="space-y-2.5">
              {report.recommendations.map((r, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <div className="w-5 h-5 rounded-full bg-violet-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="font-sans text-xs font-bold text-violet-500">{i + 1}</span>
                  </div>
                  <p className="font-sans text-sm text-stone-600 leading-relaxed">{r}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Optional notes */}
        {report.hormoneNote && (
          <div className="rounded-2xl bg-rose-50 border border-rose-100 px-4 py-3">
            <p className="font-sans text-xs font-semibold text-rose-600 mb-0.5">🩸 Cycle Insight</p>
            <p className="font-sans text-xs text-rose-700 leading-relaxed">{report.hormoneNote}</p>
          </div>
        )}
        {report.makeupNote && (
          <div className="rounded-2xl bg-stone-50 border border-stone-200 px-4 py-3">
            <p className="font-sans text-xs font-semibold text-stone-600 mb-0.5">💄 Makeup Insight</p>
            <p className="font-sans text-xs text-stone-500 leading-relaxed">{report.makeupNote}</p>
          </div>
        )}

        {/* Disclaimer */}
        <div className="rounded-2xl bg-stone-50 border border-stone-200 px-4 py-3">
          <p className="font-sans text-xs text-stone-400 leading-relaxed">
            <strong className="text-stone-500">Not medical advice.</strong> AI insights are based on your logged data only. Consult a dermatologist for skin concerns.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pb-6">
          <a href={buildEmailLink(item)}
            className="flex-1 py-3 rounded-2xl font-sans text-sm font-semibold text-white text-center flex items-center justify-center gap-1.5 active:opacity-80"
            style={{ background: '#5C8A78' }}>
            <SafeIcon icon={FiMail} className="text-sm" /> Share via Email
          </a>
          <button onClick={() => { onDelete(item.id); onClose(); }}
            className="w-12 h-12 rounded-2xl bg-rose-50 border border-rose-100 flex items-center justify-center active:bg-rose-100 flex-shrink-0">
            <SafeIcon icon={FiTrash2} className="text-rose-400" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function ReportRow({ item, onDelete, isNew }) {
  const [expanded, setExpanded] = useState(isNew);
  const [showFull, setShowFull] = useState(false);
  const { report, generatedAt, entryCount } = item;
  const date = new Date(generatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = new Date(generatedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  return (
    <>
      <AnimatePresence>
        {showFull && (
          <FullReportModal
            item={item}
            onClose={() => setShowFull(false)}
            onDelete={onDelete}
          />
        )}
      </AnimatePresence>

      <div className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden">
        {/* Accordion header */}
        <button
          onClick={() => setExpanded(v => !v)}
          className="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-stone-50 transition-colors"
        >
          {isNew && (
            <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-sans text-xs font-semibold">New</span>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-sans text-sm font-semibold text-stone-800 truncate">{date} · {time}</p>
            <p className="font-sans text-xs text-stone-400 mt-0.5">{entryCount} entries analyzed</p>
          </div>
          <TrendBadge trend={report.glowTrend} />
          <SafeIcon
            icon={FiChevronDown}
            className={`text-stone-400 flex-shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Accordion body */}
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              key="body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.22, ease: 'easeInOut' }}
              style={{ overflow: 'hidden' }}
            >
              <div className="px-4 pb-4 pt-1 border-t border-stone-100 space-y-3">
                {/* Summary */}
                {report.summary && (
                  <p className="font-sans text-xs text-stone-600 leading-relaxed">{report.summary}</p>
                )}

                {/* Key finding */}
                {report.topInsight && (
                  <div className="rounded-xl bg-violet-50 border border-violet-100 px-3 py-2.5">
                    <p className="font-sans text-xs font-semibold text-violet-700 mb-1">Key Finding</p>
                    <p className="font-sans text-xs text-violet-800 leading-relaxed">{report.topInsight}</p>
                  </div>
                )}

                {/* Boosters preview */}
                {report.boosters?.length > 0 && (
                  <div>
                    <p className="font-sans text-xs font-semibold text-emerald-600 mb-1.5">✦ Glow Boosters</p>
                    {report.boosters.slice(0, 2).map((b, i) => (
                      <p key={i} className="font-sans text-xs text-stone-500 leading-snug">• {b}</p>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={() => setShowFull(true)}
                    className="flex-1 py-2.5 rounded-xl font-sans text-xs font-semibold text-white active:opacity-80"
                    style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
                  >
                    View Full Report
                  </button>
                  <a href={buildEmailLink(item)}
                    className="flex-1 py-2.5 rounded-xl font-sans text-xs font-semibold text-center flex items-center justify-center gap-1 active:opacity-80"
                    style={{ border: '1.5px solid #5C8A78', color: '#5C8A78' }}>
                    <SafeIcon icon={FiMail} className="text-xs" /> Share
                  </a>
                  <button
                    onClick={() => onDelete(item.id)}
                    className="w-9 h-9 rounded-xl bg-rose-50 border border-rose-100 flex items-center justify-center active:bg-rose-100 flex-shrink-0"
                  >
                    <SafeIcon icon={FiTrash2} className="text-rose-400 text-sm" />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

export default function AIInsightsCard({ entries, onOpenPricing }) {
  const { can } = useSubscription();
  const [reports, setReports] = useState(() => loadLocalReports());
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [newId, setNewId] = useState(null);

  // Load saved reports from backend on mount (if available)
  useEffect(() => {
    getInsights()
      .then(serverReports => {
        if (serverReports?.length) {
          setReports(serverReports);
          saveLocalReports(serverReports);
        }
      })
      .catch(() => { /* use localStorage fallback */ });
  }, []);

  // Clear "New" badge after 4s
  useEffect(() => {
    if (!newId) return;
    const t = setTimeout(() => setNewId(null), 4000);
    return () => clearTimeout(t);
  }, [newId]);

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setError('');
    try {
      const data = await generateReport(entries);
      const item = {
        id: data.id || crypto.randomUUID(),
        generatedAt: data.generatedAt || new Date().toISOString(),
        entryCount: data.entryCount,
        report: data.report,
      };
      const updated = [item, ...reports];
      saveLocalReports(updated);
      setReports(updated);
      setNewId(item.id);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  }, [entries, reports]);

  const handleDelete = useCallback(async (id) => {
    const updated = reports.filter(r => r.id !== id);
    setReports(updated);
    saveLocalReports(updated);
    deleteInsight(id).catch(() => { /* best effort */ });
  }, [reports]);

  if (!can('advancedInsights')) {
    return <LockBanner onOpenPricing={onOpenPricing} />;
  }

  return (
    <div className="space-y-4">
      {/* Generate button */}
      <div className="bg-white rounded-3xl border border-stone-100 shadow-sm p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-violet-50 flex items-center justify-center flex-shrink-0">
            <SafeIcon icon={FiZap} className="text-violet-500 text-lg" />
          </div>
          <div>
            <p className="font-sans text-sm font-semibold text-stone-800">AI Skin Insights</p>
            <p className="font-sans text-xs text-stone-400">Analyzes your last 30 days of entries</p>
          </div>
        </div>

        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={handleGenerate}
          disabled={generating}
          className="w-full py-3.5 rounded-2xl font-sans font-semibold text-white text-sm flex items-center justify-center gap-2 disabled:opacity-60 transition-opacity"
          style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
        >
          {generating ? (
            <>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white"
              />
              Analyzing your skin data…
            </>
          ) : (
            <>
              <SafeIcon icon={FiPlus} />
              Run New AI Report
            </>
          )}
        </motion.button>

        {/* Error */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 rounded-2xl bg-rose-50 border border-rose-100 px-4 py-3 flex items-start gap-2"
            >
              <SafeIcon icon={FiAlertCircle} className="text-rose-400 text-sm flex-shrink-0 mt-0.5" />
              <p className="font-sans text-xs text-rose-600 leading-relaxed">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Report list */}
      {reports.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between px-1">
            <p className="font-sans text-xs font-semibold text-stone-400 uppercase tracking-widest">
              Saved Reports
            </p>
            <p className="font-sans text-xs text-stone-400">{reports.length} total</p>
          </div>

          {reports.map(item => (
            <ReportRow
              key={item.id}
              item={item}
              onDelete={handleDelete}
              isNew={item.id === newId}
            />
          ))}
        </div>
      )}

      {reports.length === 0 && !generating && (
        <div className="text-center py-6">
          <p className="font-sans text-xs text-stone-400">No reports yet. Run your first AI analysis above.</p>
        </div>
      )}
    </div>
  );
}
